import {getAllPurchaseDocumentTypes} from '@sollapay/domain/trust-types';
import {DocumentContext, DocumentType, PartyType} from '@sollapay/enums';
import {useFieldArray, useForm} from 'react-hook-form';

import {isEmptyAiValue} from '@/features/ai-extraction';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {mapBuyerEntriesToPayload} from '../utils';
import {buildEmptyCreateBuyerFormValues, pickAssetAttributes} from '../validation';
import {useBuyerFormAi} from './useBuyerFormAi';
import {useCreateBuyerMutation} from './useCreateBuyerMutation';

import type {CreateBuyerFormValues} from '../validation';
import type {BuyerDTO, CreateBuyersPayload, FatcaCrsSupplementaryPayload} from '@sollapay/types';

/** Document types that feed the AI extraction session for the add-buyer form. */
const BUYER_AI_DOCUMENT_TYPES = [
  DocumentType.BUYER_REGISTRATION_FORM,
  DocumentType.SIGNED_SALE_AGREEMENT,
  DocumentType.ASSIGNMENT_AGREEMENT
] as const satisfies DocumentType[];

interface UseCreateBuyerFormParams {
  trustAccountId: string;
  uploadedDocumentIds: string[];
  /** Unique per "Add Buyer" open — scopes document display/AI extraction to this session. */
  uploadSessionId: string;
  supplementaryValues?: FatcaCrsSupplementaryPayload | null;
  onSuccess: (buyer: BuyerDTO) => void;
}

export const useCreateBuyerForm = ({
  trustAccountId,
  uploadedDocumentIds,
  uploadSessionId,
  supplementaryValues,
  onSuccess
}: UseCreateBuyerFormParams) => {
  const strategy = useTrustTypeStrategy();
  const allowedAssetTypes = strategy.getAllowedAssetTypes();
  const emptyFormValues = buildEmptyCreateBuyerFormValues(allowedAssetTypes);
  const allowedDocumentTypes = getAllPurchaseDocumentTypes(strategy);
  const aiDocumentTypes = BUYER_AI_DOCUMENT_TYPES.filter(type =>
    allowedDocumentTypes.includes(type)
  );

  const form = useForm<CreateBuyerFormValues>({
    defaultValues: emptyFormValues,
    mode: 'onTouched'
  });

  // Single useFieldArray instance — RHF doesn't sync separate instances bound to the same field name (see usePepParties) — threaded to BuyerDetailsSection via props.
  const {
    fields: buyerFields,
    append: appendBuyer,
    remove: removeBuyer,
    replace: replaceBuyers
  } = useFieldArray({control: form.control, name: 'buyers'});

  const createBuyerMutation = useCreateBuyerMutation(trustAccountId);

  const handleSave = form.handleSubmit(async values => {
    // The signed supplementary FATCA/CRS declaration is mandatory — the API rejects buyer
    // creation without it. The flow gates save until the declaration step is complete.
    if (!supplementaryValues) return;

    const {buyers, asset, purchasePriceNis} = values;

    if (strategy.collectsAssetPerPurchase && !asset.assetType) {
      form.setError('asset.assetType', {message: 'common:required'});
      return;
    }

    const payload: CreateBuyersPayload = {
      buyers: mapBuyerEntriesToPayload(buyers, uploadedDocumentIds),
      ...(strategy.collectsAssetPerPurchase && asset.assetType
        ? {
            asset: {
              assetType: asset.assetType,
              ...pickAssetAttributes(asset.assetType, asset),
              attachments: asset.attachments
            }
          }
        : {}),
      purchasePriceNis,
      fatcaCrsSupplementary: supplementaryValues
    };

    try {
      const result = await createBuyerMutation.mutateAsync(payload);
      const buyer = result.data[0];
      if (buyer) onSuccess(buyer);
    } catch {
      // Surfaced via saveError
    }
  });

  const handleCancel = () => {
    form.reset(emptyFormValues);
    createBuyerMutation.reset();
  };

  /**
   * The PERSON default is non-empty, so fill-if-empty never applies a row's `partyType` suggestion —
   * an org field flips a still-blank (or not-yet-created) row instead; the other branch of a filled row is dropped.
   */
  const resolveSessionField = (targetField: string): string | null => {
    const branchMatch = targetField.match(/^buyers\.(\d+)\.(person|organization|signatories\.0)\./);
    if (!branchMatch) return targetField;
    const index = Number(branchMatch[1]);
    const isOrganizationField = branchMatch[2] !== 'person';
    const row = form.getValues(`buyers.${index}`);
    if (!row) {
      if (isOrganizationField) {
        form.setValue(`buyers.${index}.partyType`, PartyType.ORGANIZATION, {shouldDirty: true});
      }
      return targetField;
    }

    const isOrganizationRow = row.partyType === PartyType.ORGANIZATION;
    if (isOrganizationField === isOrganizationRow) return targetField;
    if (!isOrganizationField || !isEmptyAiValue(row.person.fullName)) return null;

    form.setValue(`buyers.${index}.partyType`, PartyType.ORGANIZATION, {shouldDirty: true});
    return targetField;
  };

  const {ai, handleCancel: handleCancelWithAI} = useBuyerFormAi({
    form,
    buyerFields,
    trustAccountId,
    isSaving: createBuyerMutation.isPending,
    isSaveError: createBuyerMutation.isError,
    onSave: handleSave,
    onCancel: handleCancel,
    sharedScope: {
      scope: {id: uploadSessionId, metadata: {sessionId: uploadSessionId}},
      documentContext: DocumentContext.PURCHASE_DOCUMENTS,
      documentTypes: aiDocumentTypes,
      resolveField: resolveSessionField
    }
  });

  return {
    form,
    buyerFields,
    appendBuyer,
    removeBuyer,
    replaceBuyers,
    handleSave,
    handleCancel: handleCancelWithAI,
    saveError: createBuyerMutation.error,
    isSaving: createBuyerMutation.isPending,
    ...ai
  };
};
